import { useEffect, useMemo, useRef, useState } from "react"
import { useIsHydrated } from "shared/lib/react/useIsHydrated"

export type AnimationState =
  | "expand"
  | "expand-done"
  | "collapse"
  | "collapse-done"

interface UseAnimationStateProps {
  isExpanded: boolean
}

/**
 * Hook to track animation state using CSS transition events
 * Provides 4 distinct states for sidebar animation
 */
export function useAppSidebarAnimationState({
  isExpanded
}: UseAnimationStateProps) {
  const isHydrated = useIsHydrated()
  const [animationState, setAnimationState] = useState<AnimationState>(
    isExpanded ? "expand-done" : "collapse-done"
  )
  const [isInstant, setIsInstant] = useState(false)
  const elementRef = useRef<HTMLElement>(null)
  const previousExpandedRef = useRef<boolean>(isExpanded)
  const hasRestoredRef = useRef(false)

  useEffect(() => {
    const element = elementRef.current
    if (!element || !isHydrated) return undefined

    const isRestore = !hasRestoredRef.current
    hasRestoredRef.current = true

    // Only animate if the state actually changed
    if (previousExpandedRef.current === isExpanded) {
      return undefined
    }

    previousExpandedRef.current = isExpanded

    // The stored state is applied right after hydration without animation
    if (isRestore) {
      setIsInstant(true)
      setAnimationState(isExpanded ? "expand-done" : "collapse-done")
      return undefined
    }

    setIsInstant(false)

    // Set the appropriate animation state immediately
    const newState: AnimationState = isExpanded ? "expand" : "collapse"
    setAnimationState(newState)

    // Check if transitions are disabled (duration is 0ms or transition is none)
    const computedStyle = window.getComputedStyle(element)
    const transitionDuration = computedStyle.getPropertyValue(
      "transition-duration"
    )
    const transitionProperty = computedStyle.getPropertyValue(
      "transition-property"
    )
    const isTransitionDisabled =
      transitionDuration === "0s" ||
      transitionDuration === "0ms" ||
      transitionProperty === "none"

    // If transitions are disabled, immediately set to done state
    if (isTransitionDisabled) {
      // Use a microtask to ensure the intermediate state is applied first
      queueMicrotask(() => {
        setAnimationState(isExpanded ? "expand-done" : "collapse-done")
      })
      return undefined
    }

    // Listen for transition events
    const handleTransitionEnd = (event: TransitionEvent) => {
      // Only respond to width transitions on the target element
      if (event.target === element && event.propertyName === "width") {
        // Set the "done" state after animation completes
        setAnimationState(isExpanded ? "expand-done" : "collapse-done")
      }
    }

    const handleTransitionCancel = (event: TransitionEvent) => {
      // Only respond to width transitions on the target element
      if (event.target === element && event.propertyName === "width") {
        // Set the "done" state based on final state
        setAnimationState(isExpanded ? "expand-done" : "collapse-done")
      }
    }

    // Fallback timeout in case transition events don't fire
    const fallbackTimeout = setTimeout(() => {
      setAnimationState(isExpanded ? "expand-done" : "collapse-done")
    }, 500) // 500ms should be enough for most reasonable transition durations

    element.addEventListener("transitionend", handleTransitionEnd)
    element.addEventListener("transitioncancel", handleTransitionCancel)

    // Cleanup
    return () => {
      element.removeEventListener("transitionend", handleTransitionEnd)
      element.removeEventListener("transitioncancel", handleTransitionCancel)
      clearTimeout(fallbackTimeout)
    }
  }, [isExpanded, isHydrated])

  // Generate appropriate classnames
  const classNames = useMemo(
    () => ({
      isExpand: animationState === "expand",
      isExpandDone: animationState === "expand-done",
      isCollapse: animationState === "collapse",
      isCollapseDone: animationState === "collapse-done",
      isInstant
    }),
    [animationState, isInstant]
  )

  return {
    elementRef,
    animationState,
    classNames
  }
}
